
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Bell, Volume2, VolumeX } from 'lucide-react';

const SoundSettings = () => {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('nalc_sounds_enabled');
    return saved !== null ? saved === 'true' : true;
  });
  
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('nalc_sounds_volume');
    return saved !== null ? parseInt(saved) : 70;
  });
  
  // Handle toggling sound on/off
  const handleSoundToggle = (checked: boolean) => {
    setSoundEnabled(checked);
    localStorage.setItem('nalc_sounds_enabled', checked.toString());
  };
  
  // Handle volume change
  const handleVolumeChange = (newValue: number[]) => {
    const newVolume = newValue[0];
    setVolume(newVolume);
    localStorage.setItem('nalc_sounds_volume', newVolume.toString());
  };
  
  // Play test sound
  const playTestSound = () => {
    if (!soundEnabled) return;
    
    try {
      // Try multiple paths for better compatibility
      let audio;
      try {
        audio = new Audio('/notification-sound.mp3');
      } catch {
        try {
          audio = new Audio('/assets/sounds/notification.mp3');
        } catch {
          audio = new Audio('/src/assets/sounds/notification.mp3');
        }
      }
      
      if (audio) {
        audio.volume = volume / 100;
        audio.play().catch(e => console.error('Failed to play notification sound:', e));
      }
    } catch (error) {
      console.error('Error playing test sound:', error);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <Volume2 className="h-5 w-5" />
          Sound Settings
        </CardTitle>
        <CardDescription>Configure notification sounds and volume</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sound-switch" className="text-base">
                Enable Sounds
              </Label>
              <p className="text-sm text-muted-foreground">
                Turn notification sounds on or off
              </p>
            </div>
            <Switch
              id="sound-switch"
              checked={soundEnabled}
              onCheckedChange={handleSoundToggle}
            />
          </div>
          
          <div className="space-y-3 pt-3">
            <div className="flex justify-between">
              <Label htmlFor="volume-slider">Volume</Label>
              <span className="text-sm text-muted-foreground">{volume}%</span>
            </div>
            <div className="flex items-center gap-2">
              <VolumeX className="h-4 w-4 text-muted-foreground" />
              <Slider
                id="volume-slider"
                disabled={!soundEnabled}
                value={[volume]}
                onValueChange={handleVolumeChange}
                max={100}
                step={1}
                className="flex-1"
              />
              <Volume2 className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </div>
        
        <div className="flex justify-center pt-2">
          <Button 
            onClick={playTestSound} 
            disabled={!soundEnabled}
            className="w-full sm:w-auto"
          >
            <Bell className="mr-2 h-4 w-4" />
            Test Sound
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SoundSettings;
